import React from 'react';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { ScrollPanel } from 'primereact/scrollpanel';
import { InputText } from 'primereact/inputtext';
import Draggable from 'react-draggable';

interface Step3PositionsProps {
    signers: any[];
    selectedSigner: any;
    setSelectedSigner: (signer: any) => void;
    signaturePositions: any[];
    setSignaturePositions: React.Dispatch<React.SetStateAction<any[]>>;
    pdfLoading: boolean;
    canvases: HTMLCanvasElement[];
    canvasRefs: React.MutableRefObject<any>;
    draggableRefs: React.MutableRefObject<any>;
    handleDrop: (e: any, pageIndex: number) => void;
    handleDragStop: (e: any, data: any, id: string) => void;
    readonly?: boolean;
}

export const Step3Positions: React.FC<Step3PositionsProps> = ({
    signers,
    selectedSigner,
    setSelectedSigner,
    signaturePositions,
    setSignaturePositions,
    pdfLoading,
    canvases,
    canvasRefs,
    draggableRefs,
    handleDrop,
    handleDragStop,
    readonly = false
}) => {
    const getPositionLabel = (position: any) => {
        const signer = signers.find(item => item.partyId === position.partyId);
        return signer?.partyName || position.roleTypeName || position.partyId || '';
    };

    const getRequiredSignatureHeight = (label: any, width: number, fontSize: number, configuredHeight: number) => {
        const text = String(label || '');
        const usableWidth = Math.max(40, Number(width || 200) - 18);
        const avgCharWidth = Math.max(5, Number(fontSize || 12) * 0.55);
        const charsPerLine = Math.max(8, Math.floor(usableWidth / avgCharWidth));
        const signerLineCount = Math.max(1, Math.ceil(text.length / charsPerLine));
        const lineHeight = Math.ceil(Number(fontSize || 12) * 1.25);

        return Math.max(configuredHeight, (signerLineCount + 2) * lineHeight + 14);
    };

    const getEffectiveSignatureHeight = (position: any) => {
        return getRequiredSignatureHeight(
            getPositionLabel(position),
            Number(position.widthPx || 200),
            Number(position.fontSize || 12),
            Number(position.heightPx || 65)
        );
    };

    React.useEffect(() => {
        if (!signaturePositions.length) return;

        let changed = false;
        const nextPositions = signaturePositions.map(position => {
            const effectiveHeight = getEffectiveSignatureHeight(position);
            const currentHeight = Number(position.heightPx || 0);

            if (effectiveHeight > currentHeight) {
                changed = true;
                return { ...position, heightPx: effectiveHeight };
            }

            return position;
        });

        if (changed) {
            setSignaturePositions(nextPositions);
        }
    }, [signaturePositions, signers, setSignaturePositions]);

    return (
        <div className="flex gap-2" style={{ height: '100%' }}>
            {/* Sidebar: Chọn người ký + danh sách vị trí */}
            <div style={{ width: '300px', minWidth: '300px' }} className="border-right-1 border-gray-200 pr-2 flex flex-column h-full">
                {/* Dropdown chọn vai trò */}
                <div className="mb-3">
                    <label className="block font-medium mb-2">Chọn người ký</label>
                    <Dropdown
                        style={{ width: '100%' }}
                        options={signers
                            .filter(signer => !!signer.partyId)
                            .map(signer => ({
                                label: signer.partyName || signer.partyId,
                                value: signer.partyId,
                                ...signer
                            }))}
                        value={selectedSigner?.partyId}
                        onChange={(e) => {
                            const signer = signers.find(item => item.partyId === e.value);
                            setSelectedSigner(signer);
                        }}
                        placeholder="Chọn người ký"
                        className="w-full"
                        disabled={readonly}
                    />
                </div>

                {/* Nút kéo thả - chỉ hiện khi không readonly và đã chọn vai trò */}
                {selectedSigner && !readonly && (
                    <div className="mb-3">
                        <span className="block font-medium mb-2">
                            <b>(Chọn loại chữ ký và kéo thả vào vị trí cần ký)</b>
                        </span>
                        <Button
                            icon="pi pi-plus"
                            label="Ký điện tử"
                            severity="danger"
                            className="w-full mb-2"
                            draggable
                            onDragStart={(e) => e.dataTransfer.setData('text', 'signature')}
                        />
                    </div>
                )}

                <Divider type="solid" className="my-2" />

                {/* Danh sách vị trí ký */}
                <ScrollPanel style={{ flex: 1 }} className="custom-scroll">
                    <div className="flex flex-column">
                        {signaturePositions.length === 0 ? (
                            <div className="text-center p-4">
                                <i className="pi pi-info-circle text-400 mb-2 block" style={{ fontSize: '1.5rem' }} />
                                <p className="text-sm text-500 italic m-0">Chưa có vị trí ký nào</p>
                            </div>
                        ) : signaturePositions.map((pos, index) => {
                            const signer = signers.find(item => item.partyId === pos.partyId);
                            const label = signer?.partyName || pos.roleTypeName || pos.partyId || '';
                            const pageIdx = pos.pageIndex || 0;
                            const canvas = canvases && canvases[pageIdx] ? canvases[pageIdx] : (canvases && canvases[0]);
                            const maxW = canvas ? Math.max(0, Math.floor(canvas.width - (pos.x || 0))) : undefined;
                            const maxH = canvas ? Math.max(0, Math.floor(canvas.height - (pos.y || 0))) : undefined;

                            return (
                                <div key={pos.locationSeqId || index} className="mb-3" style={readonly ? { pointerEvents: 'none', opacity: 0.6 } : {}}>
                                    <div className="flex align-items-center justify-content-between mb-1">
                                        <b style={{ fontSize: '14px' }}>Vị trí {index + 1}: {label}</b>
                                        {!readonly && (
                                            <Button
                                                icon="pi pi-trash"
                                                className="p-button-text p-button-danger p-button-sm p-0"
                                                style={{ width: '28px', height: '28px' }}
                                                onClick={() => setSignaturePositions(prev => prev.filter(p => p.locationSeqId !== pos.locationSeqId))}
                                                tooltip="Xóa vị trí này"
                                                tooltipOptions={{ position: 'top' }}
                                            />
                                        )}
                                    </div>
                                    <div className="flex gap-2">
                                        <div className="flex flex-column" style={{ flex: 1 }}>
                                            <label className="text-sm mb-1">Chiều rộng</label>
                                            <InputText
                                                type="number"
                                                value={String(pos.widthPx || 200)}
                                                min={0}
                                                max={maxW}
                                                onChange={(e) => {
                                                    const newPositions = [...signaturePositions];
                                                    const raw = parseInt(e.target.value);
                                                    const desired = Math.max(raw || 0, 0);
                                                    if (canvas) {
                                                        const currentX = pos.x || 0;
                                                        const canvasW = canvas.width || 0;
                                                        const desiredWidth = Math.min(desired, canvasW);
                                                        if (currentX + desiredWidth > canvasW) {
                                                            const newX = Math.max(0, canvasW - desiredWidth);
                                                            newPositions[index].x = newX;
                                                            newPositions[index].widthPx = Math.max(0, canvasW - newX);
                                                        } else {
                                                            newPositions[index].widthPx = desiredWidth;
                                                        }
                                                    } else {
                                                        newPositions[index].widthPx = desired;
                                                    }
                                                    setSignaturePositions([...newPositions]);
                                                }}
                                                className="w-full"
                                                disabled={readonly}
                                            />
                                        </div>
                                        <div className="flex flex-column" style={{ flex: 1 }}>
                                            <label className="text-sm mb-1">Chiều cao</label>
                                            <InputText
                                                type="number"
                                                value={String(pos.heightPx || 65)}
                                                min={0}
                                                max={maxH}
                                                onChange={(e) => {
                                                    const newPositions = [...signaturePositions];
                                                    const raw = parseInt(e.target.value);
                                                    if (canvas) {
                                                        const maxHeight = Math.max(0, Math.floor(canvas.height - (pos.y || 0)));
                                                        newPositions[index].heightPx = Math.min(Math.max(raw || 0, 0), maxHeight);
                                                    } else {
                                                        newPositions[index].heightPx = Math.max(raw || 0, 0);
                                                    }
                                                    setSignaturePositions([...newPositions]);
                                                }}
                                                className="w-full"
                                                disabled={readonly}
                                            />
                                        </div>
                                        <div className="flex flex-column" style={{ flex: 1 }}>
                                            <label className="text-sm mb-1">Cỡ chữ</label>
                                            <InputText
                                                type="number"
                                                value={String(pos.fontSize || 12)}
                                                min={6}
                                                max={72}
                                                onChange={(e) => {
                                                    const newPositions = [...signaturePositions];
                                                    const raw = parseInt(e.target.value);
                                                    newPositions[index].fontSize = Math.min(Math.max(raw || 6, 6), 72);
                                                    setSignaturePositions([...newPositions]);
                                                }}
                                                className="w-full"
                                                disabled={readonly}
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                        }
                    </div>
                </ScrollPanel>
            </div>

            {/* Main PDF Viewer */}
            <div className="flex-1 relative overflow-hidden bg-ground flex">
                <div className="flex-1 relative overflow-hidden">
                    {pdfLoading && <div className="flex align-items-center justify-content-center h-full"><i className="pi pi-spin pi-spinner text-4xl" /></div>}
                    {!pdfLoading && canvases.length > 0 && (
                        <ScrollPanel style={{ width: '100%', height: '100%' }}>
                            <div className="flex flex-column align-items-center p-3 gap-3">
                                {canvases.map((canvas, index) => (
                                    <div key={index}
                                        id={`step3-page-${index}`}
                                        className="shadow-2 relative mb-4"
                                        style={{ backgroundColor: '#fff' }}
                                        onDragOver={(e) => e.preventDefault()}
                                        onDrop={(e) => !readonly && handleDrop(e, index)}
                                    >
                                        <canvas ref={ref => {
                                            if (ref && canvas) {
                                                const context = ref.getContext('2d');
                                                if (context) {
                                                    ref.width = canvas.width;
                                                    ref.height = canvas.height;
                                                    context.drawImage(canvas, 0, 0);
                                                    canvasRefs.current[index] = ref;
                                                }
                                            }
                                        }}></canvas>

                                        {/* Signature boxes for this page */}
                                        {signaturePositions
                                            .filter(p => p.pageIndex === index)
                                            .map(pos => {
                                                const nref = draggableRefs.current[pos.locationSeqId] || (draggableRefs.current[pos.locationSeqId] = React.createRef());
                                                const label = getPositionLabel(pos);
                                                const signatureWidth = Number(pos.widthPx || 200);
                                                const signatureHeight = getEffectiveSignatureHeight(pos);
                                                return (
                                                    <Draggable
                                                        key={pos.locationSeqId}
                                                        nodeRef={nref}
                                                        defaultPosition={{ x: pos.x || 0, y: pos.y || 0 }}
                                                        disabled={readonly}
                                                        bounds={{
                                                            left: 0,
                                                            top: 0,
                                                            right: Math.max(0, canvas.width - signatureWidth),
                                                            bottom: Math.max(0, canvas.height - signatureHeight)
                                                        }}
                                                        onStop={(e, data) => handleDragStop(e, data, pos.locationSeqId)}
                                                    >
                                                        <div ref={nref} className="flex" style={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            background: 'rgba(255,255,255, 0.9)',
                                                            padding: '5px',
                                                            borderRadius: '5px',
                                                            cursor: readonly ? 'default' : 'move',
                                                            borderColor: 'red',
                                                            borderStyle: 'dashed',
                                                            borderWidth: '1px',
                                                            height: signatureHeight,
                                                            width: signatureWidth,
                                                            zIndex: 10
                                                        }}>
                                                            <div className="col-11" style={{ fontSize: pos.fontSize || 12, padding: 0, color: '#000', lineHeight: 1.2, overflowWrap: 'anywhere', wordBreak: 'break-word', whiteSpace: 'normal' }}>
                                                                - Ký bởi: {label}<br />
                                                                - Ký ngày: dd/MM/yyyy
                                                            </div>
                                                            {!readonly && (
                                                                <i className="pi pi-times-circle absolute top-0 right-0 cursor-pointer bg-white border-circle text-red-500"
                                                                    style={{ transform: 'translate(50%, -50%)', fontSize: '14px', zIndex: 11 }}
                                                                    onClick={() => setSignaturePositions(signaturePositions.filter(p => p.locationSeqId !== pos.locationSeqId))}
                                                                />
                                                            )}
                                                        </div>
                                                    </Draggable>
                                                );
                                            })}
                                    </div>
                                ))}
                            </div>
                        </ScrollPanel>
                    )}
                </div>

                {/* Sidebar Navigation */}
                {!pdfLoading && canvases.length > 0 && (
                    <div className="sidebar-nav surface-border border-left-1 flex flex-column py-3 px-2 surface-card shadow-1" style={{ width: '220px' }}>
                        <b className="text-xs text-center mb-3">SỐ TRANG</b>
                        <ScrollPanel style={{ flex: 1 }} className="custom-scroll">
                            <div className="flex flex-column gap-3">
                                {canvases.map((canvas, index) => (
                                    <div
                                        key={index}
                                        className="cursor-pointer p-1 hover:bg-blue-50 transition-all border-1 border-transparent hover:border-blue-400"
                                        onClick={() => {
                                            const element = document.getElementById(`step3-page-${index}`);
                                            if (element) {
                                                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                            }
                                        }}
                                    >
                                        <div className="shadow-2 border-round overflow-hidden mb-1">
                                            <img
                                                src={canvas.toDataURL('image/png')}
                                                alt={`Trang ${index + 1}`}
                                                style={{ width: '100%', height: 'auto', display: 'block' }}
                                            />
                                        </div>
                                        <div className="text-center text-xs font-semibold text-600">{index + 1}</div>
                                    </div>
                                ))}
                            </div>
                        </ScrollPanel>
                    </div>
                )}
            </div>

            {/* Page thumbnails could be added here if needed, but keeping it simple for now */}
        </div>
    );
};
